import { z } from 'zod'

/**
 * Per-round odds for dynamic-odds games (Birdie Time). Published by the engine
 * INSIDE `race_ready.payload.odds` (version 1) before betting opens, so every
 * viewer prices the board from the same committed numbers. Multipliers are the
 * engine's UNROUNDED floats — the widget formats to 2 dp; Go pays the exact
 * value from the same ppm and margin (A78). NO location key: the green arrives
 * on `race_started.payload.location`, after betting closes.
 */
export interface RoundOdds {
  version: 1
  probabilityConfigurationIds: Record<string, string>
  catalogueVersion: number
  catalogueHash: string
  catalogueSize: number
  paytableHash: string
  rtpPpm: number
  /** marble id → ppm, the 8 patterns (sums to 1_000_000). */
  weightsPpm: Record<string, number>
  /** marble id → unrounded multiplier, the 10 bettable options. */
  multipliers: Record<string, number>
  card: {
    golfer: {
      id: string
      name: string
      age: number
      level: string
      handicap?: number
      headshotUrl?: string
    }
    difficulty: number
    makeRatePpm: number
    tempo: 'favourite' | 'balanced' | 'longshot'
    /**
     * The putt length in metres and the hole number, published WITH the card
     * (engine E2) — so the capsule can show the length while betting is open
     * instead of waiting for the green on `race_started`. Both stay optional:
     * a round from an older engine, or a catalogue row that carries neither,
     * must still price its board.
     */
    distanceM?: number
    hole?: number
  }
  scheduledCloseAt: string
  revealDelayS: number
}

/** race_started.payload.venue (Birdie) — the green, revealed after betting closes. */
export interface BirdieVenue {
  id: string
  name: string
  city?: string
  country?: string
  hole?: number
  distance_m?: number
  difficulty?: number
  establishing_shot_url?: string
}

/**
 * Canonical pattern order — the engine's `weights_ppm` list order, never
 * reordered. Only used to key a LIST-shaped `weights_ppm` (see below).
 */
const PATTERN_ORDER = ['HHH', 'HHM', 'HMH', 'MHH', 'HMM', 'MHM', 'MMH', 'MMM']

const hex64 = z.string().regex(/^[0-9a-f]{64}$/)
const ppm = z.number().int().min(0).max(1_000_000)
const positive = z.number().positive()
const tempo = z.enum(['favourite', 'balanced', 'longshot'])

const golferWire = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  age: z.number().int().min(0),
  level: z.string().min(1),
  // absent OR null → undefined: a golfer who has never played carries no handicap, and an engine that
  // serialises that as null must not cost the whole card (Hollie, 2026-09-22)
  handicap: z
    .number()
    .nullish()
    .transform((value) => value ?? undefined),
  headshotUrl: z.string().optional()
})

/**
 * The engine keys `weights_ppm` by exact-market marble id, but the same eight
 * numbers also travel as the canonical PATTERNS-ordered list (the shape the
 * fairness reveal uses). Accept both: the weights only feed the optional
 * paytable-hash cross-check, so a list must never cost the board its odds.
 */
const weightsWire = z.union([z.record(ppm), z.array(ppm).length(PATTERN_ORDER.length)])

/** The wire shape, exactly as the engine writes it (snake_case). */
export const roundOddsWireSchema = z.object({
  version: z.literal(1),
  probability_configuration_ids: z.record(z.string().min(1)),
  catalogue_version: z.number().int().min(1),
  catalogue_hash: hex64,
  catalogue_size: z.number().int().min(1),
  paytable_hash: hex64,
  rtp_ppm: ppm,
  weights_ppm: weightsWire,
  multipliers: z.record(positive),
  card: z.object({
    golfer: golferWire,
    difficulty: z.number().int().min(1).max(5),
    // snake_case is what the engine writes; the catalogue-shaped camelCase key
    // has also been seen on the wire. Exactly one of the two must be present —
    // the mapper rejects a card that carries neither.
    make_rate_ppm: ppm.optional(),
    makeRatePpm: ppm.optional(),
    tempo,
    // E2: the putt length and hole number now travel with the card. Nullable
    // because the engine writes an explicit null for a catalogue row that has
    // neither, and deliberately no `.int()` / range beyond "a finite number":
    // these two only ever render a label, and a label must never be the reason
    // a whole round loses its board.
    distance_m: z.number().finite().nullable().optional(),
    hole: z.number().finite().nullable().optional()
  }),
  scheduled_close_at: z.string().min(1),
  reveal_delay_s: z.number().int().min(0)
})

/** The domain shape (camelCase) — used by the race_ready zod guard. */
// Output typed, input unknown: the golfer's handicap is transformed on the way in (null → none).
export const roundOddsSchema: z.ZodType<RoundOdds, z.ZodTypeDef, unknown> = z.object({
  version: z.literal(1),
  probabilityConfigurationIds: z.record(z.string().min(1)),
  catalogueVersion: z.number().int().min(1),
  catalogueHash: hex64,
  catalogueSize: z.number().int().min(1),
  paytableHash: hex64,
  rtpPpm: ppm,
  weightsPpm: z.record(ppm),
  multipliers: z.record(positive),
  card: z.object({
    golfer: golferWire,
    difficulty: z.number().int().min(1).max(5),
    makeRatePpm: ppm,
    tempo,
    distanceM: z.number().finite().optional(),
    hole: z.number().finite().optional()
  }),
  scheduledCloseAt: z.string().min(1),
  revealDelayS: z.number().int().min(0)
})

const weightsById = (weights: Record<string, number> | number[]): Record<string, number> => {
  if (!Array.isArray(weights)) return weights
  const byPattern: Record<string, number> = {}
  PATTERN_ORDER.forEach((pattern, index) => {
    byPattern[pattern] = weights[index] ?? 0
  })
  return byPattern
}

export const roundOddsFromWire = (raw: unknown): RoundOdds | undefined => {
  const parsed = roundOddsWireSchema.safeParse(raw)
  if (!parsed.success) return undefined
  const w = parsed.data
  const makeRatePpm = w.card.make_rate_ppm ?? w.card.makeRatePpm
  if (makeRatePpm === undefined) return undefined
  // An explicit null is "this card has no distance / hole", which is the same
  // thing to every reader as an absent key — never a 0 m putt on hole 0.
  const distanceM = w.card.distance_m ?? undefined
  const hole = w.card.hole ?? undefined
  return {
    version: 1,
    probabilityConfigurationIds: w.probability_configuration_ids,
    catalogueVersion: w.catalogue_version,
    catalogueHash: w.catalogue_hash,
    catalogueSize: w.catalogue_size,
    paytableHash: w.paytable_hash,
    rtpPpm: w.rtp_ppm,
    weightsPpm: weightsById(w.weights_ppm),
    multipliers: w.multipliers,
    card: {
      golfer: w.card.golfer,
      difficulty: w.card.difficulty,
      makeRatePpm,
      tempo: w.card.tempo,
      ...(distanceM === undefined ? {} : { distanceM }),
      ...(hole === undefined ? {} : { hole })
    },
    scheduledCloseAt: w.scheduled_close_at,
    revealDelayS: w.reveal_delay_s
  }
}
